import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { eraseVault } from "../lib/api";
import { useSecurity } from "../context/SecurityContext";

export const EraseVaultRow = () => {
  const navigate = useNavigate();
  const { lockNow } = useSecurity();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const erase = async () => {
    setBusy(true);
    try {
      const r = await eraseVault(pin, confirm);
      localStorage.removeItem("hv_last_member");
      toast.success(`Vault erased — ${r.files_shredded} files destroyed${r.subscription_cancelled ? ", subscription cancelled" : ""}`);
      setOpen(false);
      lockNow();
      navigate("/", { replace: true });
      setTimeout(() => window.location.reload(), 300);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not erase the vault");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8" data-testid="erase-vault-section">
      <h4 className="text-xs uppercase tracking-wider font-mono text-destructive flex items-center gap-1.5 mb-2"><AlertTriangle className="h-3.5 w-3.5" /> Danger zone</h4>
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium">Erase this vault</div>
          <div className="text-xs text-muted-foreground">Deletes every document, file, profile, plan and setting for good. Cancels any subscription.</div>
        </div>
        <Button data-testid="erase-vault-open-btn" size="sm" variant="outline" className="h-8 gap-1.5 rounded-full border-destructive/40 text-destructive hover:bg-destructive hover:text-destructive-foreground shrink-0" onClick={() => setOpen(true)}><Trash2 className="h-3.5 w-3.5" /> Erase…</Button>
      </div>
      <Dialog open={open} onOpenChange={(o) => !o && !busy && setOpen(false)}>
        <DialogContent className="max-w-md" data-testid="erase-vault-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-destructive">Erase everything?</DialogTitle>
            <DialogDescription>This cannot be undone. Wills, receipts, passwords, linked accounts, family profiles and the activity log are all destroyed, and any HearthVault subscription is cancelled. Download the exports you need first.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="block text-xs"><span className="text-muted-foreground">Type <b className="font-mono text-foreground">ERASE</b> to confirm</span><Input data-testid="erase-vault-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="ERASE" className="mt-1 h-10 bg-background font-mono" autoComplete="off" /></label>
            <label className="block text-xs"><span className="text-muted-foreground">Your owner PIN</span><Input data-testid="erase-vault-pin-input" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" className="mt-1 h-10 bg-background tracking-widest" autoComplete="off" /></label>
            <div className="flex gap-2 pt-1">
              <Button data-testid="erase-vault-submit-btn" variant="destructive" className="rounded-full gap-1.5" disabled={busy || confirm.trim().toUpperCase() !== "ERASE" || pin.length < 4} onClick={erase}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Erase the vault</Button>
              <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Keep it</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
